import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { api } from '../services/api'
import {
  LayoutDashboard,
  Upload,
  History,
  Users,
  LogOut,
  Menu,
  X,
  Coins,
  TrendingUp,
  Percent,
  Layers,
} from 'lucide-react'
import { useState } from 'react'

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/upload', icon: Upload, label: 'Upload' },
  { to: '/snapshots', icon: History, label: 'Snapshots' },
  { to: '/accounts', icon: Users, label: 'Accounts' },
  { to: '/subsectors', icon: Layers, label: 'Subsectors' },
  { to: '/fx-rates', icon: Coins, label: 'FX Rates' },
  { to: '/options', icon: TrendingUp, label: 'Options' },
  { to: '/benchmark-rates', icon: Percent, label: 'Rates' },
]

export default function Layout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const { data: snapshots } = useQuery({
    queryKey: ['snapshots-latest'],
    queryFn: () => api.getSnapshots(0, 1),
  })

  const latestSnapshot = snapshots?.uploads?.[0]
  const latestUploadId = latestSnapshot?.id

  // Format the snapshot date nicely (use statement_date if available, then when_generated, fallback to uploaded_at)
  const snapshotDate = latestSnapshot?.statement_date || latestSnapshot?.when_generated || latestSnapshot?.uploaded_at
  const formattedDate = snapshotDate
    ? new Date(snapshotDate).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      })
    : null

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleLatestClick = () => {
    if (latestUploadId) {
      navigate(`/portfolio/${latestUploadId}`)
      setSidebarOpen(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b">
          <h1 className="text-xl font-bold text-primary-600">Portfolio Tracker</h1>
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={24} />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <div key={to} className="flex items-center gap-1">
              <NavLink
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `flex-1 flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{label}</span>
              </NavLink>
              {to === '/snapshots' && (
                <button
                  onClick={handleLatestClick}
                  disabled={!latestUploadId}
                  className="flex flex-col items-center px-3 py-2 rounded-lg text-gray-600 hover:bg-primary-50 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="View latest snapshot"
                >
                  <span className="text-[10px] uppercase tracking-wide font-medium text-gray-400">Latest</span>
                  <span className="text-xs font-semibold">{formattedDate || '—'}</span>
                </button>
              )}
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="font-medium text-gray-900">{user?.name || user?.email}</p>
              <p className="text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-10 bg-white shadow-sm">
          <div className="flex items-center h-16 px-4">
            <button
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
